// src/pages/Level-5.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LevelTemplate from "./LevelTemplate";
import { STREAM_VIDEOS } from "../videos/streamVideos";
import { TrophyStorage } from "../services/trophyStorage";

/* ─────────────────────────────────────────────
   CONFIG – Level 5 (V1.1) — Ren (Reindeer)
   - Scene video first, then melody page
   - Streams from Cloudflare Stream
   - Uses your Level 5 hero image
   ───────────────────────────────────────────── */

// ✅ Pulled from src/videos/streamVideos.js (single source of truth)
// Safer access so build doesn't crash if a key is missing.
const LEVEL5_STREAM_ID =
  STREAM_VIDEOS?.L5_INTRO?.id || STREAM_VIDEOS?.L5_INTRO || null;

// ✅ LevelTemplate supports: videoSrc="stream:<id>"
const LEVEL5_VIDEO_SRC = LEVEL5_STREAM_ID ? `stream:${LEVEL5_STREAM_ID}` : null;

// ✅ Hero image (place file in: public/images/Ren-L5-Hero.png)
const LEVEL5_HERO_IMAGE = "/images/" + encodeURIComponent("Ren-L5-Hero.png");

/* ─────────────────────────────────────────────
   MELODY — Jingle Bells
   Line 1 visual grouping requested (TWO big blocks):
     [ E E E ]   [ E E E ]
     Jingle Bells  Jingle Bells
   ───────────────────────────────────────────── */

const LEVEL5_MELODY_LINES = [
  {
    id: 0,
    notes: ["E", "E", "E", "E", "E", "E"],
    lyric: "Jingle Bells, Jingle Bells",
  },
  {
    id: 1,
    notes: ["E", "G", "C", "D", "E"],
    lyric: "Jingle all the way",
  },
  {
    id: 2,
    notes: ["F", "F", "F", "F"],
    lyric: "Oh what fun it",
  },
  {
    id: 3,
    notes: ["F", "E", "E", "E"],
    lyric: "Is to ride a",
  },
  {
    id: 4,
    notes: ["G", "G", "F", "D", "C"],
    lyric: "One horse open sleigh",
  },
];

/* ✅ Lyric groupings per line
   Line 1: TWO big blocks
   Lines 2–5: keep your 4-group style
*/
const LEVEL5_LYRIC_GROUPS = [
  // Line 1 (2 big blocks)
  ["Jingle Bells", "Jingle Bells"],

  // Line 2
  ["Jin-gle", "All", "the", "way"],

  // Line 3
  ["Oh", "what", "fun", "it"],

  // Line 4
  ["Is", "to", "ride", "a"],

  // Line 5
  ["One horse", "op-", "en", "Sleigh"],
];

/* ✅ Note index groups per line
   Line 1: TWO big blocks: [0..2] and [3..5]
   Lines 2–5: keep your existing 4-group indexes
*/
const LEVEL5_NOTE_GROUP_INDEXES = [
  // Line 1: ["E","E","E","E","E","E"]
  [
    { start: 0, end: 2 }, // Jingle Bells
    { start: 3, end: 5 }, // Jingle Bells
  ],

  // Line 2: ["E","G","C","D","E"]
  [
    { start: 0, end: 1 }, // Jin-gle
    { start: 2, end: 2 }, // All
    { start: 3, end: 3 }, // the
    { start: 4, end: 4 }, // way
  ],

  // Line 3: ["F","F","F","F"]
  [
    { start: 0, end: 0 }, // Oh
    { start: 1, end: 1 }, // what
    { start: 2, end: 2 }, // fun
    { start: 3, end: 3 }, // it
  ],

  // Line 4: ["F","E","E","E"]
  [
    { start: 0, end: 0 }, // Is
    { start: 1, end: 1 }, // to
    { start: 2, end: 2 }, // ride
    { start: 3, end: 3 }, // a
  ],

  // Line 5: ["G","G","F","D","C"]
  [
    { start: 0, end: 1 }, // One horse
    { start: 2, end: 2 }, // op-
    { start: 3, end: 3 }, // en
    { start: 4, end: 4 }, // Sleigh
  ],
];

/* ─────────────────────────────────────────────
   HERO (NO overlay text)
   + Emoji burst:
     - correct: 🔔
     - wrong: 📵
   Crop bias: keep Ren + phone in frame (slight left bias)
   ───────────────────────────────────────────── */

function Level5Hero({ backgroundImage, noteEvent }) {
  const [bursts, setBursts] = useState([]);
  const idRef = useRef(1);
  const lastEventRef = useRef(null);

  const safeRand = useMemo(() => {
    const seed = Math.random() * 9999;
    return () => {
      const x = Math.sin(seed + idRef.current * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };
  }, []);

  useEffect(() => {
    if (!noteEvent) return;
    if (noteEvent === lastEventRef.current) return;

    lastEventRef.current = noteEvent;

    const isHit = noteEvent === "hit";
    const emoji = isHit ? "🔔" : "📵";

    const id = idRef.current++;
    const left = 18 + safeRand() * 64;
    const top = 18 + safeRand() * 52;

    const burst = { id, emoji, left, top, isHit };
    setBursts((prev) => [...prev, burst]);

    const t = setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== id));
    }, 750);

    return () => clearTimeout(t);
  }, [noteEvent, safeRand]);

  return (
    <div className="relative w-full max-w-3xl mx-auto rounded-[22px] sm:rounded-[24px] shadow bg-slate-900/5 overflow-hidden">
      {backgroundImage && (
        <img
          src={backgroundImage}
          alt=""
          className="w-full h-36 sm:h-56 object-cover"
          style={{ objectPosition: "34% 18%" }}
          draggable={false}
        />
      )}

      {bursts.map((b) => (
        <div
          key={b.id}
          className="tt-burst"
          style={{ left: `${b.left}%`, top: `${b.top}%` }}
          aria-hidden="true"
        >
          <span
            className={
              "tt-burst-emoji " + (b.isHit ? "tt-burst-hit" : "tt-burst-miss")
            }
          >
            {b.emoji}
          </span>
          {b.isHit && <span className="tt-burst-zzz">Ding!</span>}
        </div>
      ))}

      <style>{`
        .tt-burst{
          position:absolute;
          transform: translate(-50%, -50%);
          pointer-events:none;
          z-index: 5;
        }
        .tt-burst-emoji{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          font-size: 34px;
          filter: drop-shadow(0 6px 10px rgba(0,0,0,0.25));
          animation: ttFloatUp 750ms cubic-bezier(.2,.9,.2,1) forwards;
          will-change: transform, opacity;
        }
        .tt-burst-zzz{
          display:inline-block;
          margin-left: 6px;
          font-weight: 900;
          font-size: 18px;
          color: rgba(255,255,255,0.95);
          text-shadow: 0 6px 12px rgba(0,0,0,0.35);
          animation: ttFloatUp 750ms cubic-bezier(.2,.9,.2,1) forwards;
        }
        @keyframes ttFloatUp{
          0%   { transform: translateY(0px) scale(1); opacity: 1; }
          70%  { transform: translateY(-26px) scale(1.18); opacity: 1; }
          100% { transform: translateY(-46px) scale(1.05); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function Level5() {
  const navigate = useNavigate();

  return (
    <LevelTemplate
      levelId={5}
      currentLevelForSidebar={5}
      videoSrc={LEVEL5_VIDEO_SRC}
      startLabel="Start Game"
      melodyLines={LEVEL5_MELODY_LINES}
      lyricGroupsPerLine={LEVEL5_LYRIC_GROUPS}
      noteGroupIndexesPerLine={LEVEL5_NOTE_GROUP_INDEXES}
      backgroundImage={LEVEL5_HERO_IMAGE}
      heroImageFit="cover"
      renderHero={(heroProps) => (
        <Level5Hero
          backgroundImage={heroProps.backgroundImage}
          noteEvent={heroProps.noteEvent}
        />
      )}
      completionTitle="Reindeer Star unlocked!"
      completionBody={
        <>
          You played{" "}
          <span className="font-semibold text-[#4F46E5]">Jingle Bells</span>{" "}
          perfectly 🎶✨ Ren is so happy — grab your{" "}
          <span className="font-semibold text-[#4F46E5]">Reindeer Star</span> and
          continue the Star Quest!
        </>
      }
      // ✅ SUCCESS TRIGGER (Level-5 completed + star "L5" collected)
      onComplete={() => {
        TrophyStorage.markLevelAndStar(5); // marks level "5" + star "L5"
      }}
      onNextLevel={() => navigate("/level-6")}
    />
  );
}
